import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StorybookChoice {
  value: string;
  label: string;
  emoji?: string;
  /** CSS color for a small round swatch (skin tone, hair and eye colors). */
  swatch?: string;
}

interface StorybookChoiceGroupProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: StorybookChoice[];
  labelledBy: string;
  describedBy?: string;
  invalid?: boolean;
  /** 'chip' = compact pills that wrap; 'card' = emoji tiles in a grid. */
  variant?: 'chip' | 'card';
  /** Shown but not choosable (e.g. hair colour for a bald head). */
  disabled?: boolean;
  className?: string;
}

/**
 * Radix radio group styled as chips or cards. Radix supplies the roving focus
 * and arrow-key navigation, so each group is one Tab stop like a native set of
 * radio buttons.
 */
export function StorybookChoiceGroup({
  id,
  value,
  onChange,
  options,
  labelledBy,
  describedBy,
  invalid,
  variant = 'chip',
  disabled,
  className,
}: StorybookChoiceGroupProps) {
  const isCard = variant === 'card';
  return (
    <RadioGroupPrimitive.Root
      id={id}
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={cn(
        isCard ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4' : 'flex flex-wrap gap-2',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          className={cn(
            'group relative border bg-card text-card-foreground transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'hover:border-primary/60 data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:shadow-[0_0_0_1px_hsl(var(--primary))]',
            invalid ? 'border-destructive/70' : 'border-border',
            isCard
              ? 'flex min-h-[6.5rem] flex-col items-center justify-center gap-2 rounded-2xl px-3 py-4 text-center text-sm font-semibold hover:-translate-y-0.5'
              : 'inline-flex min-h-[2.5rem] items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium',
          )}
        >
          {option.swatch && (
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 rounded-full border border-black/15 shadow-inner"
              style={{ backgroundColor: option.swatch }}
            />
          )}
          {option.emoji && (
            <span aria-hidden="true" className={isCard ? 'text-3xl leading-none' : 'text-base leading-none'}>
              {option.emoji}
            </span>
          )}
          <span className="leading-tight">{option.label}</span>
          <RadioGroupPrimitive.Indicator
            className={cn(
              'flex items-center justify-center rounded-full bg-primary text-primary-foreground',
              isCard ? 'absolute right-2 top-2 h-5 w-5' : 'h-4 w-4',
            )}
          >
            <Check className="h-3 w-3" aria-hidden="true" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
